/* Light/dark toggle.

   The `is-dark-mode` class on <html> is set before first paint by the inline
   script in default.hbs — this only handles clicks and system changes, so it
   must stay in sync with the resolution order documented there:
   stored preference > system preference (light backgrounds only). */
function colorScheme() {
    const STORAGE_KEY = 'color-scheme';
    const root = document.documentElement;
    const toggle = document.querySelector('.gh-color-scheme-toggle');
    if (!toggle) return;

    const read = function () {
        try {
            return localStorage.getItem(STORAGE_KEY);
        } catch (e) {
            return null;
        }
    };

    /* Ghost's comments-ui runs in an iframe the theme cannot style. Left on its
       default `auto`, it decides light or dark *once* at mount, by reading the
       computed `color` of the element it was rendered into — .gh-comments — and
       treating a light text color as a dark page. That reading is correct on
       first paint, and it re-runs only for a MutationObserver watching `class`
       and `style` on that same element. This class is what that observer is
       for: without it, toggling the scheme leaves the comments box in the mode
       the page loaded in — a white slab at the end of every post in dark mode.
       The class name is not styled anywhere and does not need to be; the
       computed color it re-reads is already correct by the time it fires. */
    const syncComments = function (dark) {
        const boxes = document.querySelectorAll('.gh-comments');
        for (let i = 0; i < boxes.length; i++) {
            boxes[i].classList.toggle('is-dark-mode', dark);
        }
    };

    /* Getting comments-ui into dark mode is only half of it. Every element it
       renders is transparent — the one opaque thing in that iframe is the
       signup button — so what fills the box is the *canvas*, which the browser
       paints from the embedded document's own `color-scheme`. A srcdoc iframe
       starts at `normal` and does not inherit the embedder's, so the canvas
       comes out white and the widget's white-on-transparent text lands on it
       invisibly: a white slab with a floating button, which is the bug this
       whole thing started as.

       The frame is srcdoc, so it is same-origin and reachable. Setting the
       scheme on its root gets the native bits right; setting the background
       explicitly is what matches the page exactly, since the UA's dark canvas
       (#121212 in Chrome) is not this theme's #15171a. */
    const FRAME_STYLE_ID = 'gh-theme-comments-scheme';

    /* The same rule the stylesheet applies through --color-accent-fg, enforced
       inside the frame: never paint the accent straight onto the page
       background. comments-ui gives its "Sign in" link the site accent — which
       is near-black here — as an *inline* style, so only `!important` beats it.
       The selector matches an inline `color:` and therefore not the signup
       button, whose inline style starts `background-color:`; accent
       backgrounds are fine and bring their own contrast. Light mode is left
       alone: accent on white is the design. */
    const accentFallback = function (doc, dark) {
        let style = doc.getElementById(FRAME_STYLE_ID);
        if (!style) {
            style = doc.createElement('style');
            style.id = FRAME_STYLE_ID;
            (doc.head || doc.documentElement).appendChild(style);
        }
        style.textContent = dark
            ? ':is(a, button)[style^="color:"] { color: ' +
              getComputedStyle(document.body).color +
              ' !important; }'
            : '';
    };

    const paintCommentsFrame = function (frame, dark) {
        try {
            const doc = frame.contentDocument;
            if (!doc || !doc.documentElement) return;
            doc.documentElement.style.colorScheme = dark ? 'dark' : 'light';
            doc.documentElement.style.backgroundColor =
                getComputedStyle(document.body).backgroundColor;
            accentFallback(doc, dark);
        } catch (e) {
            /* Not same-origin after all — leave it to comments-ui */
        }
    };

    const paintCommentsFrames = function (dark) {
        const frames = document.querySelectorAll('.gh-comments iframe');
        for (let i = 0; i < frames.length; i++) {
            paintCommentsFrame(frames[i], dark);
            /* srcdoc content can still be parsing on the first pass */
            frames[i].addEventListener('load', function () {
                paintCommentsFrame(this, root.classList.contains('is-dark-mode'));
            });
        }
    };

    const apply = function (dark) {
        root.classList.toggle('is-dark-mode', dark);
        toggle.setAttribute('aria-pressed', dark ? 'true' : 'false');
        syncComments(dark);
        paintCommentsFrames(dark);
    };

    apply(root.classList.contains('is-dark-mode'));

    /* comments-ui mounts lazily, when the box scrolls into view, so the iframe
       does not exist at load. Watch for it and paint it as it arrives. */
    const box = document.querySelector('.gh-comments');
    if (box) {
        new MutationObserver(function () {
            paintCommentsFrames(root.classList.contains('is-dark-mode'));
        }).observe(box, {childList: true, subtree: true});
    }

    toggle.addEventListener('click', function () {
        const dark = !root.classList.contains('is-dark-mode');
        apply(dark);
        try {
            localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
        } catch (e) {
            /* Private browsing or a full quota — the choice just doesn't persist */
        }
    });

    /* Keep following the system setting until the visitor picks a side */
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function (event) {
        if (read()) return;
        apply(event.matches && !root.classList.contains('has-light-text'));
    });
}
